import { useMemo, type ReactNode } from "react";
import { CircularProgress } from "@mui/material";

import type { ParameterTimeSeriesConfiguration, ParametersLocation } from "@/lib/api";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";
import type { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { useObservedLocationsQuery } from "@/features/shared/widgets/observations-map/hooks/queries";
import { useModeledCoordinateTimeseriesChartData } from "@/features/shared/widgets/model-map/use-modeled-coordinate-timeseries-chart-data";
import TimeseriesGraphs from "@/features/dashboard/components/timeseries/timeseries-graphs";
import {
  TimeSeriesChartPreviewProvider as SharedTimeSeriesChartPreviewProvider,
  TimeSeriesChartPreviewToolbar,
  TimeSeriesChartPreviewBody,
} from "@/features/widget-forms/forms/map-widget-editor/time-series-chart-preview/time-series-chart-preview";
import sharedStyles from "@/features/widget-forms/forms/map-widget-editor/time-series-chart-preview/time-series-chart-preview.module.css";
import ScrollArea from "@/components/scroll-area/scroll-area";
import Stack from "@/components/stack/stack";

// Re-export the shared toolbar/body so the widget dialog wiring stays uniform.
export { TimeSeriesChartPreviewToolbar, TimeSeriesChartPreviewBody };

type TimeSeriesChartPreviewProviderProps = {
  /** The single time-series group being edited (live draft), or `null` until valid. */
  config: ParameterTimeSeriesConfiguration | null;
  /** Catalog parameter options for the widget (drives labels/units/viz type). */
  parameters: TimeSeriesParameterOption[];
  /** Selected weather model; the chart samples this model at the station's lat/lng. */
  modelId: string | undefined;
  children: ReactNode;
};

/**
 * Model-map adapter over the shared chart-preview scaffold. It lists the full
 * observed-station catalogue (unfiltered - any coordinate is a valid model
 * sample point, and a station's observed `parameter_ids` do not reflect model
 * availability) and renders the chart by sampling the model grid at the chosen
 * station's coordinates. The chart is a graphs-only mirror of obs-map's station
 * chart (no table tab), so both editors preview identically.
 */
export function TimeSeriesChartPreviewProvider({
  config,
  parameters,
  modelId,
  children,
}: TimeSeriesChartPreviewProviderProps) {
  const locationsQuery = useObservedLocationsQuery();
  const stations = useMemo<ParametersLocation[]>(
    () => locationsQuery.data ?? [],
    [locationsQuery.data],
  );

  return (
    <SharedTimeSeriesChartPreviewProvider
      config={config}
      parameters={parameters}
      defaultSeriesType="model"
      stations={stations}
      stationsLoading={locationsQuery.isPending}
      renderChart={(station, { timeSeriesConfigs, currentTime }) =>
        modelId ? (
          <ScrollArea viewportProps={{ className: sharedStyles.scrollViewport }}>
            <CoordinateChart
              modelId={modelId}
              latlng={{
                lat: station.coordinates.latitude,
                lng: station.coordinates.longitude,
              }}
              timeSeriesConfigs={timeSeriesConfigs}
              currentTime={currentTime}
            />
          </ScrollArea>
        ) : null
      }
    >
      {children}
    </SharedTimeSeriesChartPreviewProvider>
  );
}

type CoordinateChartProps = {
  modelId: string;
  latlng: { lat: number; lng: number };
  timeSeriesConfigs: TimeSeriesConfig[];
  currentTime: TimeRangeDimensionValue;
};

/**
 * Isolated so {@link useModeledCoordinateTimeseriesChartData} always runs with a
 * concrete coordinate (the parent only mounts this once a station is selected).
 */
function CoordinateChart({
  modelId,
  latlng,
  timeSeriesConfigs,
  currentTime,
}: CoordinateChartProps) {
  const { chartData, pending } = useModeledCoordinateTimeseriesChartData({
    modelId,
    latlng,
    timeSeriesConfigs,
  });

  if (pending) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <CircularProgress size={24} color="inherit" />
      </Stack>
    );
  }

  return (
    <TimeseriesGraphs
      timeSeriesConfigs={timeSeriesConfigs}
      data={chartData}
      currentTime={currentTime}
    />
  );
}
